import { HStack, Text, VStack } from '@chakra-ui/react'
import { PopUpMessageContext, ScreenContext } from '@hb/react'
import { ActionButton, PageContainer, SolidActionButton, useNavigate } from '@hb/react-web'
import { auth } from '@hb/react-web/src/backend/auth'
import { applyActionCode, checkActionCode } from 'firebase/auth'
import { useCallback, useContext, useEffect, useState } from 'react'

interface RecoverEmailViewProps {
  oobCode: string
}

export const RecoverEmailView = ({ oobCode }: RecoverEmailViewProps) => {
  const { contentWidth } = useContext(ScreenContext)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const navigate = useNavigate()
  const { showError, showSuccess } = useContext(PopUpMessageContext)

  useEffect(() => {
    const loadActionCodeInfo = async () => {
      try {
        const info = await checkActionCode(auth, oobCode)
        if (info.data.email) {
          setEmail(info.data.email)
        }
      } catch (error) {
        console.error(error)
        showError('Invalid or expired email recovery link')
        navigate('/')
      }
    }
    loadActionCodeInfo()
  }, [oobCode, showError, navigate])

  const handleRecoverEmail = useCallback(async () => {
    if (!oobCode) {
      showError('No recovery code found')
      return
    }
    setLoading(true)
    try {
      const info = await checkActionCode(auth, oobCode)
      await applyActionCode(auth, oobCode)
      showSuccess(
        `Email recovered successfully. Your email has been changed back to ${info.data.email}`,
      )
      setTimeout(() => navigate('/'), 2000)
    } catch (error) {
      console.error(error)
      showError('Error recovering email. The link may be invalid or expired.')
    } finally {
      setLoading(false)
    }
  }, [oobCode, showError, showSuccess, navigate])

  return (
    <PageContainer>
      <VStack
        flexDir="column"
        w="100%"
        width={contentWidth}
        bg="gray.50"
        py={4}
        px={6}
        alignItems="flex-start"
        spacing={0}>
        <Text fontFamily="Hero-New" fontSize="xl" fontWeight={500} color="gray.600">
          Recover Email
        </Text>
        <Text fontSize="lg">Are you sure you want to recover your email address to {email}?</Text>
        <Text fontSize="md" color="gray.600">
          This will revert any recent email address changes to your account.
        </Text>
        <HStack w="100%" mt={3}>
          <ActionButton onClick={() => navigate('/')}>Cancel</ActionButton>
          <SolidActionButton ml="auto" onClick={handleRecoverEmail} isLoading={loading}>
            Recover Email
          </SolidActionButton>
        </HStack>
      </VStack>
    </PageContainer>
  )
}
